import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

const DEFAULT_SUCCESS_MESSAGES = {
  on: "公開に変更しました。",
  off: "非公開に変更しました。",
};

/**
 * 希望条件の有無と公開状態を管理する共通hook
 */
export default function useRequestStatus({
  fetchPath,
  togglePath,
  existsKey,
  eventName,
  requestParamKey,
  successMessages = DEFAULT_SUCCESS_MESSAGES,
  errorMessage = "読み込みに失敗しました。",
  handleSessionExpired,
}) {
  // 希望条件が存在するか
  const [exists, setExists] = useState(null);

  // 公開状態（true: 公開 / false: 非公開）
  const [isActive, setIsActive] = useState(null);

  // 初回ロード中フラグ
  const [isStatusLoading, setIsStatusLoading] = useState(true);

  // 取得失敗フラグ（未入力と区別するため）
  const [error, setError] = useState(false);

  // 確認モーダルの開閉
  const [modalOpen, setModalOpen] = useState(false);

  // モーダルで確定待ちの状態
  const [pendingStatus, setPendingStatus] = useState(null);

  const handleSessionExpiredRef = useRef(handleSessionExpired);

  useEffect(() => {
    handleSessionExpiredRef.current = handleSessionExpired;
  }, [handleSessionExpired]);

  // 初回ロード：希望条件と公開状態を取得
  useEffect(() => {
    const controller = new AbortController();

    setIsStatusLoading(true);
    setError(false);

    fetch(fetchPath, {
      credentials: "same-origin",
      cache: "no-store",
      signal: controller.signal,
    })
      .then((res) => {
        if (handleSessionExpiredRef.current?.(res)) {
          return null;
        }
        if (!res.ok) {
          throw new Error("Failed to fetch");
        }

        return res.json();
      })
      .then((data) => {
        if (!data) {
          return;
        }
        setExists(data[existsKey]);
        setIsActive(data.recruiting_flag);
        setError(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;

        console.error("Error loading request:", err);
        toast.error(errorMessage, { toastId: "fetch-error" });

        // 取得失敗
        setError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsStatusLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [fetchPath, existsKey, errorMessage]);

  // 公開状態を更新
  const updateRecruitingFlag = useCallback(
    async (newStatus) => {
      try {
        const response = await fetch(togglePath, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
              ?.content,
          },
          body: JSON.stringify({
            [requestParamKey]: {
              recruiting_flag: newStatus,
            },
          }),
        });

        if (handleSessionExpiredRef.current?.(response)) {
          return false;
        }

        if (!response.ok) {
          toast.error("失敗しました。");
          return false;
        }

        // state更新
        setIsActive(newStatus);

        // 他コンポーネントへ通知
        window.dispatchEvent(
          new CustomEvent(eventName, {
            detail: { recruiting_flag: newStatus },
          }),
        );

        // 成功メッセージ
        toast.success(newStatus ? successMessages.on : successMessages.off);
        return true;
      } catch (error) {
        console.error("Error updating recruiting flag:", error);
        toast.error("失敗しました。");
        return false;
      }
    },
    [togglePath, requestParamKey, eventName, successMessages],
  );

  // トグル押下時（モーダルを開く）
  const handleChangeStatus = useCallback((next) => {
    setPendingStatus(next);
    setModalOpen(true);
  }, []);

  // モーダルで確定
  const handleConfirmStop = useCallback(async () => {
    if (pendingStatus === null) return;

    await updateRecruitingFlag(pendingStatus);
    setModalOpen(false);
    setPendingStatus(null);
  }, [pendingStatus, updateRecruitingFlag]);

  // モーダルキャンセル
  const handleCancelStop = useCallback(() => {
    setModalOpen(false);
    setPendingStatus(null);
  }, []);

  return {
    exists,
    isActive,
    isStatusLoading,
    error,
    modalOpen,
    handleChangeStatus,
    handleConfirmStop,
    handleCancelStop,
  };
}
